import Download16 from '@carbon/icons-react/es/download/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Table, Tooltip } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { FileUploadStatus, UploadFileWithAttachment } from 'api/attachments';
import { Button } from 'components/atoms/button';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { Popconfirm } from 'components/atoms/popconfirm';
import { useTranslation } from 'hooks/use-translation';
import styles from './attachments.module.css';

interface AttachmentsTableProps {
  filesList?: Array<UploadFileWithAttachment>;
  onRemoveFile: (file: UploadFileWithAttachment) => void;
  onDownloadFile: (file: UploadFileWithAttachment) => void;
  isBasicPreview?: boolean;
}

export const AttachmentsTable: FC<AttachmentsTableProps> = (props) => {
  const { filesList = [], onRemoveFile, onDownloadFile } = props;
  const ATTACHMENTS_COLUMNS = [
    {
      title: useTranslation('name'),
      dataIndex: 'name',
      key: 'name',
      width: '50%',
      ellipsis: true,
      render: (text, record: UploadFileWithAttachment) => (
        <Tooltip title={text} placement="topLeft">
          <a
            href={record.url}
            rel="noreferrer noopener"
            target="_blank"
            className={classNames(styles.name, 'text-01')}
            onClick={(e) => e.stopPropagation()}
          >
            {text}
          </a>
        </Tooltip>
      ),
    },
    {
      title: useTranslation('uploaded_on'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      className: styles.uploaded_on,
      render: (text, row: UploadFileWithAttachment) => {
        const createdAt = row.attachment?.createdAt;

        if (row.status === FileUploadStatus.Uploading) {
          return (
            <div className="text-03">
              <FormattedMessage id="uploading" />
            </div>
          );
        }
        if (row.status === FileUploadStatus.Error) {
          return <FormattedMessage id="attachment.failed" />;
        }
        if (createdAt) {
          return (
            <Tooltip
              title={<HumanisedDate timestamp={moment(createdAt)} />}
              placement="topLeft"
            >
              <HumanisedDate timestamp={moment(createdAt)} />
            </Tooltip>
          );
        }

        return <div className="text-03">---</div>;
      },
    },
    {
      className: styles.delete_cell,
      render: (text, item: UploadFileWithAttachment) => {
        return (
          <>
            <Button
              icon={<Download16 />}
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDownloadFile(item);
              }}
            />
            {item.status === FileUploadStatus.Error ? (
              <Button
                icon={<TrashCan16 />}
                type="link"
                size="small"
                danger
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFile(item);
                }}
              />
            ) : (
              <Popconfirm
                onConfirm={() => {
                  onRemoveFile(item);
                }}
              >
                <Button
                  data-test-id="remove-attachment-button-table"
                  icon={<TrashCan16 />}
                  type="link"
                  size="small"
                  danger
                />
              </Popconfirm>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Table
      className={styles.attachments__table}
      size="small"
      rowKey="uid"
      pagination={false}
      columns={ATTACHMENTS_COLUMNS}
      dataSource={filesList}
    />
  );
};
